import React, { useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { CaptureType } from '@/types';

import styles from './QuickCapturePanel.module.css';

interface QuickCaptureContentProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CaptureData) => void;
}

export interface CaptureData {
  content: string;
  type: CaptureType;
}

interface RecentCapture {
  id: string;
  content: string;
  type: CaptureType;
  timestamp: number;
}

const CAPTURE_TYPES: { value: CaptureType; label: string; icon: string }[] = [
  { value: 'thought', label: 'Thought', icon: '💭' },
  { value: 'task', label: 'Task', icon: '✅' },
  { value: 'tomorrow', label: 'Tomorrow', icon: '📅' },
  { value: 'soon', label: 'Soon', icon: '🔜' },
  { value: 'note', label: 'Note', icon: '📝' },
  { value: 'quick-block', label: 'Quick Block', icon: '⚡' },
];

// Get relative time string
function getRelativeTime(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ago`;
}

export function QuickCaptureContent({ isOpen, onClose, onSave }: QuickCaptureContentProps) {
  const [content, setContent] = useState('');
  const [type, setType] = useState<CaptureType>('thought');
  const [recentCaptures, setRecentCaptures] = useState<RecentCapture[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const [, forceUpdate] = useState(0);

  // Update relative times every 5 seconds
  useEffect(() => {
    if (!isOpen || recentCaptures.length === 0) return;
    const interval = setInterval(() => forceUpdate((n) => n + 1), 5000);
    return () => clearInterval(interval);
  }, [isOpen, recentCaptures.length]);

  // Focus input when panel opens (but don't reset recent captures)
  useEffect(() => {
    if (isOpen) {
      setContent('');
      // Focus with small delay
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    } else {
      // Clear recent captures when panel closes
      setRecentCaptures([]);
    }
  }, [isOpen]);

  // Batch mode save - keeps panel open
  const handleSave = useCallback(() => {
    if (!content.trim()) return;

    const captureData = {
      content: content.trim(),
      type,
    };

    // Add to recent captures (keep last 3)
    const newCapture: RecentCapture = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      content: captureData.content,
      type: captureData.type,
      timestamp: Date.now(),
    };
    setRecentCaptures((prev) => [newCapture, ...prev].slice(0, 3));

    // Save
    onSave(captureData);

    // Reset input but stay open (batch mode)
    setContent('');
    // Re-focus input for next capture
    inputRef.current?.focus();
  }, [content, type, onSave]);

  // Done button - close panel
  const handleDone = useCallback(() => {
    setContent('');
    setType('thought');
    setRecentCaptures([]);
    onClose();
  }, [onClose]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleDone();
      } else if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSave();
      }
    },
    [handleDone, handleSave]
  );

  return (
    <div className={styles.container} onKeyDown={handleKeyDown}>
      <div className={styles.body}>
        {/* Input */}
        <div className={styles.inputGroup}>
          <input
            ref={inputRef}
            id="capture-content"
            type="text"
            className={styles.captureInput}
            placeholder="Type anything..."
            value={content}
            onChange={(e) => setContent(e.target.value)}
            autoComplete="off"
          />
        </div>

        {/* Minimal Type Selector - Icons only */}
        <div className={styles.typeSelector}>
          <div className={styles.typeChipsMinimal}>
            {CAPTURE_TYPES.map((captureType) => (
              <button
                key={captureType.value}
                className={`${styles.typeChipMinimal} ${type === captureType.value ? styles.active : ''}`}
                onClick={() => setType(captureType.value)}
                type="button"
                aria-pressed={type === captureType.value}
                title={captureType.label}
              >
                <span className={styles.typeIconLarge} aria-hidden="true">
                  {captureType.icon}
                </span>
              </button>
            ))}
          </div>
          <span className={styles.selectedTypeLabel}>
            {CAPTURE_TYPES.find((t) => t.value === type)?.label}
          </span>
        </div>

        {/* Recent Captures (Batch Mode) */}
        <AnimatePresence>
          {recentCaptures.length > 0 && (
            <motion.div
              className={styles.recentCaptures}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
            >
              <span className={styles.recentLabel}>Just captured:</span>
              <div className={styles.recentList}>
                <AnimatePresence mode="popLayout">
                  {recentCaptures.map((capture) => (
                    <motion.div
                      key={capture.id}
                      className={styles.recentItem}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 10 }}
                      transition={{ duration: 0.15 }}
                      layout
                    >
                      <span className={styles.recentCheck}>✓</span>
                      <span className={styles.recentContent}>{capture.content}</span>
                      <span className={styles.recentType}>
                        {CAPTURE_TYPES.find((t) => t.value === capture.type)?.icon}
                      </span>
                      <span className={styles.recentTime}>
                        {getRelativeTime(capture.timestamp)}
                      </span>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <span className={styles.hint}>
          <kbd className={styles.kbd}>↵</kbd> Save & continue
        </span>
        <div className={styles.footerButtons}>
          <button
            className={styles.saveBtn}
            onClick={handleSave}
            disabled={!content.trim()}
            type="button"
          >
            Capture
          </button>
          <button className={styles.doneBtn} onClick={handleDone} type="button">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

export default QuickCaptureContent;
